import { Flex } from 'antd';
import { Button, Spin } from '../Ant';
import { EmptyNotifications, LoadNotifications } from '../icons';
import NoData from '../dashboard/noData';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import NotificationContent from './NotificationContent';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import { IGroupedNotifications, IInAppNotification } from '../../interface';
import { useTranslation } from 'react-i18next';

interface NotificationTabContentProps {
  tab: string;
  activeTab: string;
  groupedNotifications: IGroupedNotifications[];
  notificationList: IInAppNotification[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  language: string;
  onMarkAllAsRead: () => void;
  onSearch?: (value: string) => void;
  onRefresh?: () => void | Promise<unknown>;
  onNotificationClick: (notification: IInAppNotification) => void;
  loadMoreRef?: (node?: Element | null) => void;
  markAllAsReadText: string;
  searchPlaceholder?: string;
  noNotificationsText: string;
  noNotificationsSubText?: string;
  rootRef?: (node?: Element | null) => void;
  error?: Error | null;
  onRetry?: () => void;
}

const NotificationTabContent: React.FC<NotificationTabContentProps> = ({
  tab,
  activeTab,
  groupedNotifications,
  notificationList,
  isLoading,
  isFetching,
  hasNextPage,
  language,
  onMarkAllAsRead,
  onSearch,
  onRefresh,
  onNotificationClick,
  loadMoreRef,
  markAllAsReadText,
  searchPlaceholder,
  noNotificationsText,
  noNotificationsSubText,
  rootRef,
  error,
  onRetry,
}) => {
  const { t } = useTranslation();
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };

  if (error) {
    return (
      <Flex
        vertical
        gap={50}
        align="center"
        justify="center"
        className="h-full"
        data-testid={'notification-tab-' + tab}
      >
        <Flex vertical gap={15} align="center" justify="center" data-testid={`error-state-${tab}`}>
          <NoData
            header={t('common:couldntLoadNotifications')}
            subHeader={t('common:couldntLoadNotificationsSubtext')}
            icon={<LoadNotifications />}
            gap={0}
          />
        </Flex>
        <Button
          type="default"
          onClick={onRetry}
          data-testid={`try-again-button-${tab}`}
          loading={isLoading}
          size="large"
          block
          maxwidth="200px"
        >
          {t('common:tryAgain')}
        </Button>
      </Flex>
    );
  }

  const hasNotifications = notificationList && Object.entries(notificationList).length > 0;

  return (
    <Flex gap={15} vertical className="h-full" data-testid={'notification-tab-' + tab}>
      {hasNotifications && (
        <div>
          <Button
            height="24px"
            type="primary"
            block
            onClick={onMarkAllAsRead}
            data-testid={`mark-all-as-read-button-${tab}`}
          >
            {markAllAsReadText}
          </Button>
        </div>
      )}
      {activeTab === 'all' && onSearch && searchPlaceholder && (
        <SearchInputWithFilters
          handleSearch={onSearch}
          searchTitle={searchPlaceholder}
          handleRefresh={onRefresh ?? (() => {})}
        />
      )}
      <Flex
        ref={rootRef}
        gap={15}
        vertical
        className="notification-menu"
        data-testid="notification-menu"
        key={activeTab}
      >
        <NotificationContent
          groupedNotifications={groupedNotifications}
          language={language}
          onNotificationClick={onNotificationClick}
          loadMoreRef={loadMoreRef}
          hasNextPage={hasNextPage}
        />
      </Flex>
      {!hasNotifications && !isLoading && (
        <div
          role="status"
          aria-live="polite"
          className="flex-grow d-flex justify-center items-center"
          data-testid={`no-notifications-${tab}`}
        >
          <NoData
            header={noNotificationsText}
            icon={<EmptyNotifications />}
            subHeader={noNotificationsSubText}
          />
        </div>
      )}
      {isLoading ? (
        <Flex gap={15} vertical data-testid={`loading-${tab}`}>
          <SkeletonCardList rows={[1]} withSubcontentOnFirstRow withHeader={false} />
        </Flex>
      ) : (
        renderLoader()
      )}
    </Flex>
  );
};

export default NotificationTabContent;
